import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { FileText, Image as ImageIcon, Upload, Loader2, Trash2, Eye } from 'lucide-react';

const KINDS = [
  { key: 'report', label: 'Hearing Report' },
  { key: 'invoice', label: 'Invoice' },
  { key: 'other', label: 'Other' },
];

export const TicketFilesPanel = ({ ticketId }) => {
  const [files, setFiles] = useState([]);
  const [kind, setKind] = useState('report');
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/tickets/${ticketId}/files`); setFiles(data); } catch { /* empty list */ }
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error('Max file size is 10 MB');
    const fd = new FormData();
    fd.append('file', file); fd.append('kind', kind);
    setUploading(true);
    try {
      await api.post(`/tickets/${ticketId}/files`, fd);
      toast.success(`${KINDS.find(k => k.key === kind).label} uploaded`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const open = async (f) => {
    const win = window.open('', '_blank');
    try {
      const { data } = await api.get(`/tickets/files/${f.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(data);
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { if (win) win.close(); toast.error('Could not open file'); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.filename}?`)) return;
    try { await api.delete(`/tickets/files/${f.id}`); load(); } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-3" data-testid="ticket-files-panel">
      <div className="flex gap-1">
        {KINDS.map((k) => (
          <button key={k.key} type="button" onClick={() => setKind(k.key)} data-testid={`file-kind-${k.key}`}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${kind === k.key ? 'bg-stone-900 text-white border-stone-900' : 'bg-white border-stone-300 text-stone-700 hover:border-stone-500'}`}>
            {k.label}
          </button>
        ))}
      </div>
      <label
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg py-6 cursor-pointer transition-colors ${drag ? 'border-sky-500 bg-sky-50' : 'border-stone-300 hover:border-stone-500 bg-stone-50'}`}
        data-testid="file-dropzone"
      >
        {uploading ? <Loader2 className="w-5 h-5 animate-spin text-stone-500" /> : <Upload className="w-5 h-5 text-stone-500" />}
        <span className="text-xs text-stone-700 font-medium">{uploading ? 'Uploading…' : `Drop or click to upload ${KINDS.find(k => k.key === kind).label.toLowerCase()}`}</span>
        <span className="text-[10px] text-stone-500">PDF, JPG, PNG, WEBP · max 10 MB</span>
        <input ref={inputRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => upload(e.target.files?.[0])} data-testid="file-upload-input" disabled={uploading} />
      </label>

      {files.length === 0 ? (
        <div className="text-xs text-stone-400 italic">No reports or invoices yet.</div>
      ) : (
        <ul className="divide-y divide-stone-100 border border-stone-200 rounded-md">
          {files.map((f) => (
            <li key={f.id} className="flex items-center gap-2 px-2.5 py-2 text-xs" data-testid={`ticket-file-${f.id}`}>
              {f.content_type === 'application/pdf' ? <FileText className="w-4 h-4 text-rose-600" /> : <ImageIcon className="w-4 h-4 text-sky-600" />}
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-stone-800">{f.filename}</div>
                <div className="text-[10px] text-stone-500">
                  <span className="uppercase tracking-wider font-semibold">{f.kind}</span> · {Math.max(1, Math.round((f.size || 0) / 1024))} KB · {f.uploaded_by} · {new Date(f.created_at).toLocaleDateString('en-IN')}
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => open(f)} data-testid={`ticket-file-open-${f.id}`}><Eye className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-700" onClick={() => remove(f)} data-testid={`ticket-file-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
